import { useTheme } from "../../styles/theme";
import { useApp } from "../../context/AppContext";
import { dispDate, daysBetween } from "../../utils/dates";
import { fmt } from "../../utils/format";
import SectionTitle from "../../components/SectionTitle";
import Field from "../../components/Field";

export default function DebtManager() {
  const S = useTheme();
  const c = S.colors;
  const {
    data, form, setForm, TODAY,
    debts, totalDebt, overdueDebts,
    debtStrategy, setDebtStrategy,
    availableCash, setAvailableCash,
    strategyList,
    partialPayId, setPartialPayId,
    partialAmt, setPartialAmt,
    addDebt, makePartialPayment, clearDebt, deleteDebt,
  } = useApp();

  return (
    <div>
      {/* Summary strip */}
      <div style={S.debtSummaryStrip}>
        <div style={S.debtSummaryItem}>
          <div style={S.debtSummaryValue}>{debts.filter(d => d.remaining > 0).length}</div>
          <div style={S.debtSummaryLabel}>Active Debts</div>
        </div>
        <div style={S.debtSummaryDivider} />
        <div style={S.debtSummaryItem}>
          <div style={{ ...S.debtSummaryValue, color: c.danger }}>{fmt(totalDebt)}</div>
          <div style={S.debtSummaryLabel}>Total Remaining</div>
        </div>
        <div style={S.debtSummaryDivider} />
        <div style={S.debtSummaryItem}>
          <div style={{ ...S.debtSummaryValue, color: c.dangerLight }}>{overdueDebts.length}</div>
          <div style={S.debtSummaryLabel}>Overdue</div>
        </div>
        <div style={S.debtSummaryDivider} />
        <div style={S.debtSummaryItem}>
          <div style={{ ...S.debtSummaryValue, color: c.primary }}>
            {fmt(debts.reduce((a, d) => a + d.totalAmount - d.remaining, 0))}
          </div>
          <div style={S.debtSummaryLabel}>Total Paid Back</div>
        </div>
      </div>

      {/* Add debt form */}
      <SectionTitle style={{ marginTop: 24 }}>Add Vendor Debt</SectionTitle>
      <div style={S.formCard}>
        <div style={S.formGrid}>
          <Field label="Vendor / Creditor">
            <input style={S.input} placeholder="e.g. Sharma Suppliers"
              value={form.debtVendor || ""}
              onChange={e => setForm({ ...form, debtVendor: e.target.value })} />
          </Field>
          <Field label="Total Amount Owed (₹)">
            <input style={S.input} type="number" placeholder="0"
              value={form.debtAmt || ""}
              onChange={e => setForm({ ...form, debtAmt: e.target.value })} />
          </Field>
          <Field label="Due Date">
            <input style={S.input} type="date"
              value={form.debtDue || ""}
              onChange={e => setForm({ ...form, debtDue: e.target.value })} />
          </Field>
          <Field label="Interest %/month">
            <input style={S.input} type="number" placeholder="0"
              value={form.debtInt || ""}
              onChange={e => setForm({ ...form, debtInt: e.target.value })} />
          </Field>
          <Field label="Note">
            <input style={S.input} placeholder="What is this debt for?"
              value={form.debtNote || ""}
              onChange={e => setForm({ ...form, debtNote: e.target.value })} />
          </Field>
        </div>
        <div style={{ ...S.creditNote, borderLeftColor: c.danger }}>
          🔴 Separate from daily 1-day credit purchases
        </div>
        <button style={{ ...S.submitBtn, background: c.danger, color: "#fff" }} onClick={addDebt}>
          + Add Debt
        </button>
      </div>

      {/* Smart Clearance Plan */}
      {debts.filter(d => d.remaining > 0).length > 0 && (
        <>
          <SectionTitle style={{ marginTop: 28 }}>Smart Clearance Plan</SectionTitle>
          <div style={S.cashInputRow}>
            <div style={S.cashInputLabel}>
              <span style={{ fontSize: 20 }}>💵</span>
              <div>
                <div style={{ fontWeight: 700, color: c.text }}>Available cash to pay debts today</div>
                <div style={{ fontSize: 12, color: c.textFaint }}>We'll allocate it smartly across all debts</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ color: c.textDim }}>₹</span>
              <input style={{ ...S.input, width: 150 }} type="number" placeholder="e.g. 50000"
                value={availableCash}
                onChange={e => setAvailableCash(e.target.value)} />
            </div>
          </div>

          <div style={S.strategyRow}>
            <span style={S.strategyLabel}>Strategy:</span>
            {[{ k: "suggested", l: "🎯 Smart" }, { k: "avalanche", l: "🌋 Avalanche" }, { k: "snowball", l: "⛄ Snowball" }].map(s => (
              <button key={s.k} onClick={() => setDebtStrategy(s.k)}
                style={{ ...S.strategyBtn, ...(debtStrategy === s.k ? S.strategyActive : {}) }}>
                {s.l}
              </button>
            ))}
          </div>
          <div style={S.strategyExplain}>
            {debtStrategy === "suggested" && "🎯 Smart: Overdue first → Highest interest → Smallest balance. Best all-round strategy."}
            {debtStrategy === "avalanche" && "🌋 Avalanche: Highest interest first. Saves the most money long-term."}
            {debtStrategy === "snowball" && "⛄ Snowball: Smallest balance first. Clears more debts faster for momentum."}
          </div>

          <div style={S.debtCardList}>
            {strategyList.map((d, i) => {
              const paidBack = d.totalAmount - d.remaining;
              const pct = Math.round((paidBack / d.totalAmount) * 100);
              const isOverdue = d.dueDate && d.dueDate < TODAY && d.remaining > 0;
              const daysLeft = d.dueDate ? daysBetween(TODAY, d.dueDate) : null;
              return (
                <div key={d.id} style={{
                  ...S.debtCard,
                  borderColor: isOverdue
                    ? c.dangerBorder
                    : i === 0
                      ? c.infoBorder
                      : c.border,
                }}>
                  <div style={S.debtCardHeader}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={{
                        ...S.priorityBadge,
                        background: i === 0 ? c.danger : i === 1 ? c.warn : c.inputBorder,
                        color: i < 2 ? "#fff" : c.textDim,
                      }}>
                        #{i + 1}{i === 0 ? " PAY FIRST" : i === 1 ? " PAY NEXT" : ""}
                      </span>
                      <span style={S.debtVendorName}>{d.vendor}</span>
                      {isOverdue && <span style={S.overdueBadge}>OVERDUE {daysBetween(d.dueDate, TODAY)}d</span>}
                    </div>
                    <button style={{ ...S.smallBtn, ...S.deleteBtn }} onClick={() => deleteDebt(d.id)}>✕</button>
                  </div>

                  {d.note && <div style={S.debtNote}>{d.note}</div>}

                  <div style={S.debtProgressRow}>
                    <div style={S.debtProgressBar}>
                      <div style={{
                        ...S.debtProgressFill,
                        width: `${pct}%`,
                        background: pct === 100 ? c.primary : isOverdue ? c.danger : c.info,
                      }} />
                    </div>
                    <span style={S.debtPct}>{pct}% paid</span>
                  </div>

                  <div style={S.debtAmountRow}>
                    <div>
                      <div style={S.debtAmountLabel}>Remaining</div>
                      <div style={{ ...S.debtAmountValue, color: c.danger }}>{fmt(d.remaining)}</div>
                    </div>
                    <div>
                      <div style={S.debtAmountLabel}>Total</div>
                      <div style={S.debtAmountValue}>{fmt(d.totalAmount)}</div>
                    </div>
                    <div>
                      <div style={S.debtAmountLabel}>Paid</div>
                      <div style={{ ...S.debtAmountValue, color: c.primary }}>{fmt(paidBack)}</div>
                    </div>
                    {d.interestRate > 0 && (
                      <div>
                        <div style={S.debtAmountLabel}>Interest</div>
                        <div style={{ ...S.debtAmountValue, color: c.warn }}>{d.interestRate}%/mo</div>
                      </div>
                    )}
                    {daysLeft !== null && (
                      <div>
                        <div style={S.debtAmountLabel}>Due</div>
                        <div style={{ ...S.debtAmountValue, color: isOverdue ? c.danger : daysLeft <= 3 ? c.warn : c.textDim }}>
                          {isOverdue ? `${daysBetween(d.dueDate, TODAY)}d ago` : daysLeft === 0 ? "Today!" : `${daysLeft}d left`}
                        </div>
                      </div>
                    )}
                  </div>

                  {availableCash && d.suggestedPayment !== undefined && (
                    <div style={{
                      ...S.suggestionBox,
                      borderColor: d.fullyCleared ? c.okBorder : c.infoBorder,
                      background: d.fullyCleared ? c.okBgStrong : c.infoBg,
                    }}>
                      <span>{d.fullyCleared ? "✅" : "💡"}</span>
                      <span style={{ color: d.fullyCleared ? c.primary : c.info, fontWeight: 600 }}>
                        {d.fullyCleared
                          ? `Pay ${fmt(d.suggestedPayment)} → Fully clears this debt!`
                          : d.suggestedPayment > 0
                            ? `Pay ${fmt(d.suggestedPayment)} → Balance left: ${fmt(d.remaining - d.suggestedPayment)}`
                            : "No cash left for this debt"}
                      </span>
                    </div>
                  )}

                  <div style={S.debtPayRow}>
                    {partialPayId === d.id ? (
                      <>
                        <input style={{ ...S.input, width: 140, marginRight: 8 }} type="number"
                          placeholder="₹ Amount" value={partialAmt}
                          onChange={e => setPartialAmt(e.target.value)} autoFocus />
                        <button style={{ ...S.smallBtn, marginRight: 6 }} onClick={() => makePartialPayment(d.id)}>✓ Confirm</button>
                        <button style={{ ...S.smallBtn, ...S.deleteBtn }} onClick={() => { setPartialPayId(null); setPartialAmt(""); }}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button style={{ ...S.smallBtn, background: c.infoBg, color: c.info, marginRight: 6 }}
                          onClick={() => { setPartialPayId(d.id); setPartialAmt(""); }}>
                          💳 Partial Pay
                        </button>
                        <button style={{ ...S.smallBtn, background: c.primary, color: c.onPrimary }} onClick={() => clearDebt(d.id)}>
                          ✓ Clear Full Debt
                        </button>
                      </>
                    )}
                  </div>

                  {d.payments?.length > 0 && (
                    <div style={S.paymentHistory}>
                      <div style={S.paymentHistoryTitle}>Payment History</div>
                      {d.payments.map((p, pi) => (
                        <div key={pi} style={S.paymentHistoryRow}>
                          <span style={{ color: c.textFaint }}>{dispDate(p.date)}</span>
                          <span style={{ color: c.primary, fontWeight: 600 }}>+{fmt(p.amount)}</span>
                          {p.fullClear && <span style={{ ...S.badgePaid, fontSize: 10 }}>Full Clear</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Cleared debts table */}
      {debts.filter(d => d.remaining === 0).length > 0 && (
        <>
          <SectionTitle style={{ marginTop: 28 }}>✅ Cleared Debts</SectionTitle>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>{["Vendor","Total","Paid","Added","Status"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {debts.filter(d => d.remaining === 0).map(d => (
                  <tr key={d.id} style={S.tr}>
                    <td style={S.td}>{d.vendor}</td>
                    <td style={{ ...S.td, fontWeight: 700 }}>{fmt(d.totalAmount)}</td>
                    <td style={{ ...S.td, color: c.primary, fontWeight: 700 }}>{fmt(d.totalAmount)}</td>
                    <td style={S.td}>{dispDate(d.addedDate)}</td>
                    <td style={S.td}><span style={S.badgePaid}>✓ Cleared</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {debts.length === 0 && (
        <div style={S.emptyDebt}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: c.text }}>No debts recorded</div>
          <div style={{ fontSize: 13, color: c.textFaint, marginTop: 6 }}>Add a vendor debt above to start tracking</div>
        </div>
      )}
    </div>
  );
}
